import { config } from '../../app-configuration/config.js';
import { queryFeatures as esriQueryFeatures } from '@esri/arcgis-rest-feature-service';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

/**
 * @typedef {object} APILayerConfig
 * @property {boolean} authenticated This layer requires authentication and must be accessed through the proxy
 * @property {string} url The direct URL to the ArcGIS feature service for this layer (used if not authenticated)
 * @property {string} model_key The model key for this layer, used for proxying authenticated requests
 * @property {string} model_layer_key The model layer key for this layer, used for proxying authenticated requests
 * @property {string[]} [fields] Optional list of fields to include in the response when querying this layer. Defaults to all fields (*).
 * @property {object} [options] Additional options for this layer
 * @property {string} [options.uniqueField] Name of the unique field for this layer, used in {@link getFeatureByUniqueField}. If not
 * provided, defaults to "OBJECTID".
 */

/**
 * Get the appropriate URL for a configured ArcGIS layer, given its config
 * @param {APILayerConfig} layerConfig The configuration for the layer from `map_api_layers`
 * @return {string} The URL to use for accessing the layer's feature service
 */
export function getLayerURL(layerConfig) {
  // Unauthenticated layers can use the URL directly
  if (!layerConfig.authenticated) return layerConfig.url;

  // Authenticated layers need to go through the proxy
  const path = `arcGISFeatureServiceProxy/${encodeURIComponent(layerConfig.model_key)}/${encodeURIComponent(layerConfig.model_layer_key)}`;

  // Check if we are using an emulator (development environments only)
  const emulator = config.emulators?.functions;
  if (emulator) return `http://${emulator.host}:${emulator.port}/${config.appName}/us-central1/${path}/`;

  return `https://us-central1-${config.appName}.cloudfunctions.net/${path}/`;
}

/**
 * Query features from an ArcGIS feature service layer based on the provided configuration and query parameters
 * @param {APILayerConfig} layerConfig The configuration for the layer to query, from `map_api_layers`
 * @param {string} query The where clause to use for querying features (e.g. "height > 10")
 * @param {object} [options]
 * @param {string} [options.authToken] If provided, this auth token will be used directly instead of fetching from Firebase Auth
 */
export async function queryFeatures(layerConfig, query, options) {
  const url = getLayerURL(layerConfig);
  const authToken = layerConfig.authenticated ? await _getAuthToken(options) : undefined;
  if (layerConfig.authenticated && !authToken) {
    throw new Error('Layer requires authentication, but no auth token is available');
  }

  return await esriQueryFeatures({
    url,
    headers: layerConfig.authenticated ? { Authorization: `Bearer ${authToken}` } : undefined,
    where: query,
    outSR: '4326', // Always return results in WGS 84, as we do in gvector
    outFields: layerConfig.fields || '*'
  });
}

/**
 * Get a single feature from an ArcGIS feature service layer by its unique field (from the layer configuration)
 * @param {APILayerConfig} layerConfig The configuration for the layer to query, from `map_api_layers`
 * @param {string|number} uniqueFieldValue The value of the unique field for the feature to retrieve
 * @param {object} [options]
 * @param {string} [options.authToken] If provided, this auth token will be used directly instead of fetching from Firebase Auth
 */
export async function getFeatureByUniqueField(layerConfig, uniqueFieldValue, options) {
  const uniqueFieldName = layerConfig.options?.uniqueField || 'OBJECTID';
  // Add quotes around string values in the query, escaping single quotes by doubling them (ArcGIS REST API syntax)
  const queryValue = typeof uniqueFieldValue === 'string' ? `'${uniqueFieldValue.replace(/'/g, "''")}'` : uniqueFieldValue;
  return await queryFeatures(layerConfig, `${uniqueFieldName} = ${queryValue}`, options);
}

/**
 * Get an authentication token for accessing authenticated layers
 * @param {object} [options]
 * @param {string} [options.authToken] If provided, this auth token will be used directly instead of fetching from Firebase Auth
 * @return {Promise<string|undefined>} The authentication token to use in the Authorization header for authenticated requests
 */
async function _getAuthToken(options) {
  // If options provides an auth token, use it
  if (options?.authToken) return options.authToken;

  // Otherwise, get the user's token from Firebase Auth
  return await firebase.auth().currentUser?.getIdToken();
}
